import React, { useEffect, useState } from "react";
import { Trophy, ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";

function Row({ row, isLast }) {
  const top1 = row.rank === 1;
  const medalBg =
    row.rank === 1 ? "bg-[#FFD166] text-[#02371b]" :
    row.rank === 2 ? "bg-neutral-200 text-[#02371b]" :
    row.rank === 3 ? "bg-[#CD7F32] text-white" :
    "bg-white/10 text-white/70";

  return (
    <div className={`flex items-center gap-3 py-2.5 ${isLast ? "" : "border-b border-white/10"}`}>
      <span className={`w-6 h-6 rounded-full flex items-center justify-center font-mono-tab font-bold text-[11px] ${medalBg}`}>
        {row.rank}
      </span>
      <div className="w-7 h-7 rounded-full overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
        {row.picture ? (
          <img src={row.picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-[10px] font-bold text-white/80">{(row.name || "?")[0]?.toUpperCase()}</span>
        )}
      </div>
      <span className={`flex-1 text-sm truncate ${top1 ? "font-bold" : "font-medium"} text-white`}>{row.name}</span>
      <span className="font-mono-tab font-bold text-sm text-white">{row.points}</span>
    </div>
  );
}

export default function TopRanksCard({ tournamentId, groupId, onOpenFull }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tournamentId) return;
    const fetchTop = async () => {
      const params = { tournament_id: tournamentId, limit: 3 };
      if (groupId) params.group_id = groupId;
      try {
        const { data } = await api.get("/leaderboard/top", { params });
        setData(data);
      } catch (err) {
        console.error("Failed to fetch top ranks", err);
      }
    };
    fetchTop();
    const interval = setInterval(fetchTop, 30000);
    return () => clearInterval(interval);
  }, [tournamentId, groupId]);

  if (!data) {
    return <div className="h-[140px] rounded-2xl bg-[#02371b]/10 animate-pulse" />;
  }

  const showMe = data.me_outside_top && data.me;

  return (
    <motion.button
      type="button"
      onClick={onOpenFull}
      initial={{ y: 6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="grain relative w-full text-left rounded-2xl bg-[#02371b] text-white overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
      data-testid="top-ranks-card"
    >
      <div className="flex items-center justify-between px-5 pt-4">
        <div className="flex items-center gap-2">
          <Trophy size={13} weight="fill" className="text-[#FFD166]" />
          <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-white/60">{t("top_of_tournament")}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase text-white/50">
          {data.total_players} {t("players")} <ArrowRight size={10} weight="bold" />
        </div>
      </div>
      <div className="px-5 py-3">
        {data.top.length === 0 ? (
          <div className="py-4 text-center text-sm text-white/50">{t("empty_leaderboard")}</div>
        ) : (
          data.top.map((r, i) => <Row key={r.user_id} row={r} isLast={i === data.top.length - 1 && !showMe} />)
        )}
        {showMe && (
          <div className="mt-1 pt-2 border-t border-dashed border-white/10 flex items-center gap-3 py-2">
            <span className="w-6 h-6 rounded-full bg-[#41a42b] flex items-center justify-center font-mono-tab font-bold text-[11px] text-white">
              {data.me.rank ?? "—"}
            </span>
            <span className="w-7 h-7 rounded-full overflow-hidden bg-white/10 border border-white/30 flex items-center justify-center flex-shrink-0">
              {data.me.picture ? (
                <img src={data.me.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[10px] font-bold text-white">{(data.me.name || "?")[0]?.toUpperCase()}</span>
              )}
            </span>
            <span className="flex-1 text-sm font-bold truncate text-white">{t("you")} · {data.me.name}</span>
            <span className="font-mono-tab font-bold text-sm text-[#FFD166]">{data.me.points}</span>
          </div>
        )}
      </div>
    </motion.button>
  );
}
